/**
 * Particulate — marine snow (SPEC §10.5). Three parallax layers of motes drifting down
 * through the water column with a lateral current wobble.
 *
 * The whole field is a single Points draw whose motes are placed entirely in the vertex
 * shader and wrapped, modulo a box centred on the player, so a few thousand particles cost
 * nothing on the CPU and the column never runs out no matter how far the shark swims.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';
import { Rng } from '../core/Rng.ts';
import { TOKENS, hexToLinear } from '../voxel/Palette.ts';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

const VERT = /* glsl */ `
uniform float uTime;
uniform vec3  uFocus;
uniform float uField;
uniform float uFieldY;
uniform float uFall;
uniform float uSize;
uniform float uPixelRatio;

attribute vec3  aSeed;    // base position, already in field units
attribute float aLayer;   // 0 near, 1 mid, 2 far — drives size, speed and opacity

varying float vFade;
varying float vLayer;

void main() {
  float depthMix = aLayer / 2.0;
  float speed = uFall * (1.25 - depthMix * 0.55);

  vec3 p = aSeed;
  p.y -= uTime * speed;
  // Lateral current: each mote gets its own phase from its own seed, so the field shears
  // rather than sliding as one sheet.
  float wob = 0.9 + depthMix * 1.1;
  p.x += sin(uTime * 0.21 + aSeed.z * 0.83) * wob;
  p.z += cos(uTime * 0.17 + aSeed.x * 0.71) * wob;

  // Wrap inside a box that rides with the player. Because the box origin is subtracted and
  // added back, a mote stays put in world space until it actually crosses a face.
  vec3 base = vec3(uFocus.x - uField * 0.5, 0.0, uFocus.z - uField * 0.5);
  vec3 q;
  q.x = mod(p.x - base.x, uField) + base.x;
  q.z = mod(p.z - base.z, uField) + base.z;
  q.y = mod(p.y, uFieldY);

  vec4 mv = viewMatrix * vec4(q, 1.0);
  float dist = -mv.z;

  // Near motes are culled softly: a mote a metre from the lens is a screen-filling blob.
  float near = smoothstep(2.0, 9.0, dist);
  float far = 1.0 - smoothstep(uField * 0.30, uField * 0.62, dist);
  vFade = near * far * (0.85 - depthMix * 0.45);
  vLayer = depthMix;

  gl_PointSize = uSize * uPixelRatio * (1.35 - depthMix * 0.5) * (26.0 / max(dist, 1.0));
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */ `
uniform vec3 uColorNear;
uniform vec3 uColorFar;

varying float vFade;
varying float vLayer;

void main() {
  // Round, soft-edged mote. Squaring the falloff gives a core with a halo rather than a
  // hard disc, which is what keeps 6000 of them from reading as static.
  vec2 d = gl_PointCoord - 0.5;
  float r = dot(d, d) * 4.0;
  float a = (1.0 - clamp(r, 0.0, 1.0));
  a *= a * vFade;
  if (a < 0.004) discard;

  vec3 col = mix(uColorNear, uColorFar, vLayer);
  gl_FragColor = vec4(col * a, a);
}
`;

export class Particulate {
  readonly points: THREE.Points;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly focus: THREE.Vector3;

  constructor(seed = 0x51105e, count = OCEAN.snowCount) {
    const rng = new Rng(seed >>> 0);
    const field = OCEAN.snowField;
    const fieldY = OCEAN.surfaceHeight * 1.25;

    const seedAttr = new Float32Array(count * 3);
    const layerAttr = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      seedAttr[i * 3] = rng.range(0, field);
      seedAttr[i * 3 + 1] = rng.range(0, fieldY);
      seedAttr[i * 3 + 2] = rng.range(0, field);
      layerAttr[i] = rng.int(0, 3);
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('aSeed', new THREE.BufferAttribute(seedAttr, 3));
    this.geometry.setAttribute('aLayer', new THREE.BufferAttribute(layerAttr, 1));
    this.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(count * 3), 3));

    this.focus = new THREE.Vector3();

    this.material = new THREE.ShaderMaterial({
      // Named so a compile failure says WHICH shader; three logs the material name and
      // nothing else identifying, and an empty name means reading every shader in the project.
      name: 'Particulate',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uFocus: { value: this.focus },
        uField: { value: field },
        uFieldY: { value: fieldY },
        uFall: { value: OCEAN.snowFallSpeed },
        uSize: { value: 2.1 },
        uPixelRatio: { value: 1 },
        uColorNear: { value: new THREE.Vector3(...hexToLinear(TOKENS.bone)) },
        uColorFar: { value: new THREE.Vector3(...hexToLinear(WATER.rimColor)) },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.name = 'marine-snow';
    this.points.frustumCulled = false;
    this.points.renderOrder = 6;
  }

  update(time: number, focusX: number, focusZ: number, pixelRatio: number): void {
    this.focus.set(focusX, 0, focusZ);
    this.material.uniforms.uTime.value = time;
    this.material.uniforms.uPixelRatio.value = pixelRatio;
  }


  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.material.uniforms, env);
  }

  dispose(): void {
    this.points.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
